import React, { useEffect, useState } from "react";
import {
  History, RotateCcw, Trash2, MessageCircle, Loader2, Check, AlertTriangle,
  User, Clock, ArrowRightLeft,
} from "lucide-react";
import { toast } from "sonner";
import {
  listSectionVersions, removeSectionUpload, requestSectionReupload,
  restoreSectionVersion, moveSectionVersion, errorMessage,
} from "../../lib/api";

const fmtWhen = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", { day: "2-digit", month: "short",
      hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

/** Owner-only controls for a single section: view version history, restore an
 * older version, remove the current upload, or request a fresh upload. */
export default function SectionOwnerPanel({ group, section, onChanged }) {
  const [open, setOpen] = useState(false);
  const [versions, setVersions] = useState(section.versions || []);
  const [busy, setBusy] = useState(false);
  const [showReq, setShowReq] = useState(false);
  const [reason, setReason] = useState("");
  const [notify, setNotify] = useState(true);
  const [moveTarget, setMoveTarget] = useState("");

  useEffect(() => { setVersions(section.versions || []); }, [section.versions]);

  const reload = async () => {
    try {
      const res = await listSectionVersions(group.id, section.id);
      setVersions(res.versions || []);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const doRestore = async (vid) => {
    if (!window.confirm("Restore this version as the current upload?")) return;
    setBusy(true);
    try {
      await restoreSectionVersion(group.id, section.id, vid);
      toast.success("Version restored.");
      await reload();
      onChanged?.();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally { setBusy(false); }
  };

  const doMove = async (versionId, targetSectionId) => {
    const targetTitle = (group.sections || []).find((s) => s.id === targetSectionId)?.title || "the target section";
    if (!window.confirm(
      `Move this upload to "${targetTitle}"? The document will move without requiring the student to re-upload.`
    )) return;
    setBusy(true);
    try {
      await moveSectionVersion(group.id, section.id, versionId, targetSectionId);
      toast.success(`Moved to ${targetTitle}.`);
      setMoveTarget("");
      onChanged?.();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally { setBusy(false); }
  };

  const doRemove = async () => {
    if (!window.confirm(
      "Remove the current upload? The section will go back to 'Awaiting Upload' — you can still restore this version later from history."
    )) return;
    setBusy(true);
    try {
      await removeSectionUpload(group.id, section.id, { reason: "", notify_email: false });
      toast.success("Upload removed.");
      await reload();
      onChanged?.();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally { setBusy(false); }
  };

  const doRequest = async () => {
    setBusy(true);
    try {
      await requestSectionReupload(group.id, section.id, { reason, notify_email: notify });
      toast.success(notify ? "Re-upload requested — member notified." : "Re-upload requested.");
      setShowReq(false); setReason("");
      await reload();
      onChanged?.();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally { setBusy(false); }
  };

  const currentVersion = (versions || []).find((v) => v.is_current);
  const removedVersions = (versions || []).filter((v) => v.is_removed);

  return (
    <div className="mt-3 rounded-lg border border-indigo-100 bg-indigo-50/40 overflow-hidden" data-testid={`section-owner-panel-${section.id}`}>
      <button
        onClick={() => { setOpen((o) => !o); if (!open) reload(); }}
        data-testid={`section-owner-toggle-${section.id}`}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-xs font-medium text-indigo-700 hover:bg-indigo-100 transition-colors"
      >
        <span className="inline-flex items-center gap-1.5">
          <History size={13} />
          Owner controls · {versions.length} version{versions.length === 1 ? "" : "s"}
        </span>
        <span className="text-[10px] text-indigo-400">{open ? "hide" : "show"}</span>
      </button>

      {open && (
        <div className="p-3 border-t border-indigo-100">
          {/* Quick actions */}
          <div className="flex flex-wrap gap-2 mb-3 items-center">
            {currentVersion && (
              <>
                <button
                  onClick={doRemove}
                  disabled={busy}
                  data-testid={`section-remove-upload-${section.id}`}
                  className="text-[11px] font-semibold text-red-700 hover:text-red-900 bg-white border border-red-200 rounded-md px-2.5 py-1 inline-flex items-center gap-1 disabled:opacity-50"
                >
                  <Trash2 size={12} /> Remove current upload
                </button>
                <button
                  onClick={() => setShowReq((s) => !s)}
                  disabled={busy}
                  data-testid={`section-request-reupload-${section.id}`}
                  className="text-[11px] font-semibold text-amber-700 hover:text-amber-900 bg-white border border-amber-200 rounded-md px-2.5 py-1 inline-flex items-center gap-1 disabled:opacity-50"
                >
                  <MessageCircle size={12} /> Request re-upload
                </button>
                {/* Move current upload to a different section */}
                <div className="inline-flex items-center gap-1 bg-white border border-indigo-200 rounded-md px-2 py-1" data-testid={`section-move-picker-${section.id}`}>
                  <ArrowRightLeft size={12} className="text-indigo-600" />
                  <span className="text-[11px] text-indigo-700 font-semibold">Move to</span>
                  <select
                    value={moveTarget}
                    onChange={(e) => setMoveTarget(e.target.value)}
                    disabled={busy}
                    data-testid={`section-move-target-${section.id}`}
                    className="text-[11px] bg-transparent border-0 focus:outline-none text-indigo-800 font-medium max-w-[140px]"
                  >
                    <option value="">Pick section…</option>
                    {(group.sections || [])
                      .filter((s) => s.id !== section.id)
                      .sort((a, b) => a.order - b.order)
                      .map((s) => (
                        <option key={s.id} value={s.id}>{s.title}</option>
                      ))}
                  </select>
                  <button
                    onClick={() => moveTarget && doMove(currentVersion.id, moveTarget)}
                    disabled={busy || !moveTarget}
                    data-testid={`section-move-confirm-${section.id}`}
                    className="text-[11px] font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded px-2 py-0.5 disabled:opacity-40"
                  >
                    Save
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Request re-upload form */}
          {showReq && (
            <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-3" data-testid={`section-reupload-form-${section.id}`}>
              <p className="text-xs font-semibold text-amber-800 mb-1">Reason (optional — shown to the member)</p>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                data-testid={`section-reupload-reason-${section.id}`}
                placeholder="e.g. This looks like the wrong file — please upload the Literature Review."
                className="w-full text-xs rounded border border-amber-300 bg-white px-2 py-1.5 mb-2"
              />
              <label className="text-[11px] text-amber-800 inline-flex items-center gap-1.5 mb-2">
                <input
                  type="checkbox"
                  checked={notify}
                  onChange={(e) => setNotify(e.target.checked)}
                  data-testid={`section-reupload-notify-${section.id}`}
                />
                Email the assigned member
              </label>
              <div className="flex gap-2">
                <button
                  onClick={doRequest}
                  disabled={busy}
                  data-testid={`section-reupload-confirm-${section.id}`}
                  className="text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-md px-3 py-1.5 disabled:opacity-50"
                >
                  {busy ? <Loader2 size={12} className="animate-spin inline mr-1" /> : null}
                  Send request
                </button>
                <button
                  onClick={() => setShowReq(false)}
                  className="text-xs font-medium text-amber-700 px-3 py-1.5"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Version list */}
          {versions.length === 0 ? (
            <p className="text-xs text-zinc-500 italic">No uploads yet.</p>
          ) : (
            <ol className="divide-y divide-indigo-100 bg-white rounded-md border border-indigo-100" data-testid={`section-version-list-${section.id}`}>
              {versions.map((v) => (
                <li key={v.id} className="px-3 py-2 flex items-start gap-3" data-testid={`section-version-${v.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-mono font-semibold text-zinc-800 tabular-nums">v{v.version_number}</span>
                      {v.is_current && (
                        <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5 inline-flex items-center gap-0.5">
                          <Check size={9} /> Current
                        </span>
                      )}
                      {v.is_removed && !v.is_current && (
                        <span className="text-[10px] font-semibold text-zinc-500 bg-zinc-100 border border-zinc-200 rounded-full px-1.5 py-0.5">Removed</span>
                      )}
                      {v.review_recommended && (
                        <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5 inline-flex items-center gap-0.5">
                          <AlertTriangle size={9} /> Review
                        </span>
                      )}
                      {!v.auto_classified && (
                        <span className="text-[10px] text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-full px-1.5 py-0.5">Manual</span>
                      )}
                    </div>
                    <p className="text-xs text-zinc-700 truncate mt-0.5">{v.filename || "—"}</p>
                    <p className="text-[11px] text-zinc-500 mt-0.5 inline-flex items-center gap-1 flex-wrap">
                      <User size={10} /> {v.uploaded_by_name || "Unknown"}
                      <span className="text-zinc-300">·</span>
                      <Clock size={10} /> {fmtWhen(v.uploaded_at)}
                      <span className="text-zinc-300">·</span>
                      <span className="tabular-nums">{v.word_count} words</span>
                    </p>
                    {v.is_removed && v.removed_reason && (
                      <p className="text-[11px] text-red-700 mt-0.5">Reason: {v.removed_reason}</p>
                    )}
                  </div>
                  <div className="shrink-0 flex flex-col items-end gap-1">
                    {v.is_removed && !v.is_current && (
                      <button
                        onClick={() => doRestore(v.id)}
                        disabled={busy}
                        data-testid={`section-restore-${v.id}`}
                        className="text-[11px] font-semibold text-indigo-700 hover:text-indigo-900 bg-white border border-indigo-200 rounded px-2 py-0.5 inline-flex items-center gap-1"
                      >
                        <RotateCcw size={10} /> Restore
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          )}
          {removedVersions.length > 0 && !removedVersions.every((v) => v.is_current) && (
            <p className="text-[11px] text-zinc-500 mt-2 italic">
              Removed versions are kept in history — you can restore any of them.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
